import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { AudiPlatformProvider, TextArea, TextField } from '@audi/audi-ui-react';
import React from 'react';
import ReactDOM from 'react-dom';

@Component({
    selector: 'app-audi-text-input',
    template: '',
    providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: AudiTextFieldComponent }] // required for using this control in forms
})
export class AudiTextFieldComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
    //https://react.ui.audi/?path=/story/components-text-field-variations--page#available-props
    @Input() inputId = '';
    @Input() label = '';
    @Input() value = '';
    @Input() isRequired = 'false';
    @Input() disabled = 'false';
    @Input() inputMode = 'text';
    @Input() validationMessage = '';
    @Input() rows = 1;
    @Input() maxLength = 500;
    @Input() placeholder = '';

    @Output() valueChange = new EventEmitter<string>();
    @Output() onblur = new EventEmitter<void>();

    constructor(private hostRef: ElementRef) {
        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }

    ngOnChanges(changes: SimpleChanges): void {
        //console.log('AudiTextField.ngOnChanges');
        this.render();
    }

    ngAfterViewInit(): void {
        //console.log('AudiTextField.ngAfterViewInit');        
        this.render();
    }

    ngOnDestroy(): void {
        //console.log('AudiTextField.ngOnDestroy');
        ReactDOM.unmountComponentAtNode(this.hostRef.nativeElement);
    }

    private render() {
        //console.log('AudiTextField.render');
        if (this.rows == 1) {
            ReactDOM.render(
                <AudiPlatformProvider>
                    <TextField
                        hint={this.placeholder}
                        inputId={this.inputId}
                        label={this.label}
                        value={this.value || ' '} // to prevent 'collapsing' of header
                        inputMode={this.inputMode as any}
                        invalid={this.validationMessage != ''}
                        validationMessage={this.validationMessage}
                        onChange={this.handleChange}
                        onBlur={this.handleBlur}
                        required={this.isRequired == 'true'}
                        disabled={this.disabled == 'true'}
                    />
                </AudiPlatformProvider>,
                this.hostRef.nativeElement);
        } else {
            ReactDOM.render(
                <AudiPlatformProvider>
                    <TextArea
                        inputId={this.inputId}
                        label={this.label}
                        value={this.value || ' '}
                        invalid={this.validationMessage != ''}
                        validationMessage={this.validationMessage}
                        onChange={this.handleChange}
                        onBlur={this.handleBlur}
                        required={this.isRequired == 'true'}
                        disabled={this.disabled == 'true'}
                    />
                </AudiPlatformProvider>,
                this.hostRef.nativeElement);
        }
    }

    public handleChange(event: any): void {
        //console.log('AudiTextField.handleChange');
        this.value = event.target.value.trimStart().substring(0, this.maxLength);
        this.valueChange.emit(this.value);
        this.onChange(this.value);
        this.render();
    }

    public handleBlur(event: any): void {
        this.onblur.emit();
    }

    // implementing ControlValueAccessor - required for using this control in forms
    onChange = (value: string): void => {
        //console.log('AudiTextField.onChange');
    };

    onTouched = (): void => {
        //console.log('AudiTextField.onTouched');
    };

    writeValue(value: string): void {
        //console.log('AudiTextField.writeValue', value);
        this.value = value;
        this.render();
    }

    registerOnChange(onChange: any): void {
        //console.log('AudiTextField.registerOnChange');
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any): void {
        //console.log('AudiTextField.registerOnTouched');
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean): void {
        //console.log('AudiTextField.setDisabledState');
        this.disabled = disabled == true ? 'true' : 'false';
        this.render();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        //console.log('AudiTextField.validate');
        const value = control.value;
        if (!value) {
            return {
                required: {
                    value
                }
            };
        } else {
            return null; // validation ok
        }
    }
}
